import { type ReactNode, type KeyboardEvent } from 'react'

/**
 * The one tab strip.
 *
 * Nine places used to hand-roll `role="tablist"` over the global `.tab-btn` class, and they had
 * drifted: three had no ARIA roles at all, none handled arrow keys, the wrapper margin varied, and
 * two of them styled themselves from a CSS module instead. Three of the nine also put a spacer and
 * action buttons *inside* the tablist, which is why there is a `right` slot rather than callers
 * nesting their own flex row.
 *
 * What a caller still owns is the state, deliberately: it is `useState` in most places, a search
 * param where the tab is deep-linkable (the policies page, whose `?tab=decisions` the worker builds
 * into the text a blocked `terraform apply` prints), and fully lifted props in the provider version
 * expansion, where `null` means "no tab chosen yet". Those are real differences, so `value` and
 * `onChange` come from outside.
 */
export interface TabSpec<K extends string> {
  key: K
  label: ReactNode
  /** Rendered after the label — a count, a lock icon, a severity pill. */
  badge?: ReactNode
  /** A small uppercase pill after the label, e.g. `preview` on a not-yet-gating tab. */
  hint?: string
  disabled?: boolean
  /** Tooltip for the button. */
  title?: string
}

export function Tabs<K extends string>({
  tabs, value, onChange, label, right, style,
}: {
  tabs: ReadonlyArray<TabSpec<K>>
  value: K | null
  onChange: (key: K) => void
  /** Names the strip for screen readers. Two strips can be on screen at once — the workspace tabs
   *  and the serial tabs inside them — and both contain "Providers". */
  label: string
  right?: ReactNode
  style?: React.CSSProperties
}) {
  const enabled = tabs.filter((t) => !t.disabled)

  // Arrow keys move between tabs, which no hand-rolled strip did. Home/End jump to the ends.
  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End']
    if (!keys.includes(e.key) || enabled.length === 0) return
    e.preventDefault()
    const i = enabled.findIndex((t) => t.key === value)
    const next =
      e.key === 'Home' ? enabled[0]
      : e.key === 'End' ? enabled[enabled.length - 1]
      : e.key === 'ArrowLeft' ? enabled[(i <= 0 ? enabled.length : i) - 1]
      : enabled[(i + 1) % enabled.length]
    if (next) onChange(next.key)
  }

  return (
    <div
      role="tablist"
      aria-label={label}
      onKeyDown={onKeyDown}
      style={{
        display: 'flex', alignItems: 'center', overflowX: 'auto',
        borderBottom: '1px solid var(--border)', marginBottom: 20, ...style,
      }}
    >
      {tabs.map((t) => {
        const active = t.key === value
        return (
          <button
            key={t.key}
            role="tab"
            type="button"
            aria-selected={active}
            disabled={t.disabled}
            title={t.title}
            // Only the active tab is in the tab order; arrow keys move within the strip. That is
            // the roving-tabindex pattern `role="tablist"` implies and none of the originals had.
            tabIndex={active ? 0 : -1}
            onClick={() => onChange(t.key)}
            className={`tab-btn${active ? ' active' : ''}`}
            style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6 }}
          >
            {t.label}
            {t.badge}
            {t.hint && (
              <span style={{
                fontSize: '0.55rem', textTransform: 'uppercase', letterSpacing: '0.06em',
                color: 'var(--text3)', border: '1px solid var(--border)', borderRadius: 4, padding: '1px 4px',
              }}>{t.hint}</span>
            )}
          </button>
        )
      })}
      {right && <><div style={{ flex: 1 }} />{right}</>}
    </div>
  )
}
